import { formatDistanceToNow } from 'date-fns';
import type { ResourceLeaseHolder } from '@erp/rpc';

/**
 * Pure helpers for reading who holds a leaseable resource. Shared by the agent
 * page (start readiness) and Settings › Agent resources, kept apart from the
 * components so Fast Refresh can preserve their state.
 */

/**
 * Cleanup closes the browser and returns the lease only after the run row is
 * already completed or failed. A holder inside this window is a normal handoff.
 * Keep this value in sync with `FINISHED_HOLDER_GRACE_MS` in
 * `backend/src/core/ai/coordination/resourceStartBlock.ts`.
 */
export const FINISHED_HOLDER_GRACE_MS = 5 * 60 * 1000;

type FinishedRunHolder = Pick<
  ResourceLeaseHolder,
  'runStatus' | 'runCompletedAt'
>;

/**
 * A lease whose run finished long enough ago that cleanup should have returned
 * it. Mirrors `isLeaseHeldByFinishedRun`: a running holder, or one that finished
 * inside {@link FINISHED_HOLDER_GRACE_MS}, is still in use. A missing run row or
 * a missing completion time counts immediately.
 */
export function leaseHeldByFinishedRun(
  holder: FinishedRunHolder,
  now: number = Date.now(),
): boolean {
  if (holder.runStatus === 'running') return false;
  if (holder.runStatus === null || holder.runCompletedAt == null) return true;
  return (
    now - new Date(holder.runCompletedAt).getTime() >= FINISHED_HOLDER_GRACE_MS
  );
}

/** Whether any slot of any of these resources is held by a finished run. */
export function anyLeaseHeldByFinishedRun(
  resources: readonly {
    holders: readonly FinishedRunHolder[];
  }[],
): boolean {
  return resources.some((resource) =>
    resource.holders.some((holder) => leaseHeldByFinishedRun(holder)),
  );
}

/** The first eight characters of a run id, how runs are named elsewhere in the app. */
export function shortRunId(agentRunId: string): string {
  return agentRunId.slice(0, 8);
}

/** Relative wording ("in 5 hours" / "2 hours ago"), matching how the app ages timestamps. */
export function relativeTime(isoInstant: string): string {
  return formatDistanceToNow(new Date(isoInstant), { addSuffix: true });
}
